import { useCallback, useMemo } from 'react';

import { isCancelledError, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { useOptionalPortalFilter } from '@/contexts/PortalFilterProvider';
import { mergeStationOptions } from '@/contexts/PortalFilterProvider/portalFilterUtils';

import { formatAddress } from '@/utils/stringUtils';

import type { TEntityOption } from '@/components/filter/types';

import type { TStationListItem } from '@/types/station';

import { stationListQuery, stationSearchQuery } from '@/queries/station';
import { storage } from '@/storage';

/**
 * Site options of the pickers (portal site picker, catalog popups, members, Site details breadcrumb).
 *
 * `lookupStations` (the picker's `getOptions`): asks the backend search (`filterByStation`, at
 * most 100 sites, partner-scoped) for every query, inside or outside the portal
 * provider. The portal does not hold the partner's full site list any more, so there is nothing to
 * filter in the browser. The portal site picker shows the 12 opening sites for an empty query by
 * itself (`PortalScopeFilters`), before this lookup is called.
 * The answers are saved per typed text (`stationSearchQuery`), so the same text opens at once while
 * fresh (app default `staleTime`), and a station save or create (W1 / W3) marks every saved search old: a renamed or new site
 * shows in the next search. Closing the picker (the `signal` aborts) cancels a search still loading.
 * Its identity never changes (the query client is stable), so a picker that is open never searches again
 * because of a re-render (`EntityFilterControl` re-runs its search when `getOptions` changes).
 *
 * `restoreStations` (names of ticked sites): reads the saved names and the 12 opening sites first;
 * only an id with no known name is loaded, by id (one call, at most 100 ids, the saved
 * `stationListQuery`).
 */
export const useStationsFilter = (collection: string) => {
  const portal = useOptionalPortalFilter();
  const queryClient = useQueryClient();

  const lookupStations = useCallback(
    (query: string, signal: AbortSignal) => {
      const search = stationSearchQuery(query);
      // `fetchQuery` owns its own abort signal, so the picker's `signal` is bridged: aborting it
      // cancels this search while it loads (the promise then rejects as cancelled, like the old
      // aborted request did, as an axios cancel: `EntityFilterControl` ignores those with
      // `axios.isCancel` and keeps its list and spinner). A search another picker is also waiting for is cancelled for it too;
      // that picker asks again when it re-opens.
      const cancel = () => queryClient.cancelQueries({ queryKey: search.queryKey, exact: true });
      signal.addEventListener('abort', cancel, { once: true });

      const result = queryClient
        .fetchQuery(search)
        .catch((error: unknown) => {
          throw isCancelledError(error) ? new axios.Cancel('canceled') : error;
        })
        .finally(() => signal.removeEventListener('abort', cancel));

      if (signal.aborted) {
        cancel();
      }

      return result;
    },
    [queryClient],
  );

  // RETIRED: the shared full site list. While the portal held the partner's whole site list
  // (background pages of 100), `lookupStations` listed and filtered it in the browser once it was
  // 'loaded' (or 'failed' with sites that arrived), through a ref so its identity stayed fixed.
  // The owner replaced that design (see `PortalFilterProvider`). Kept for reference.
  // const sharedList = useRef({
  //   options: portal?.allStations ?? [],
  //   status: portal?.allStationsStatus,
  // });
  // sharedList.current = { options: portal?.allStations ?? [], status: portal?.allStationsStatus };
  //
  // const lookupStations = useCallback(async (query: string, signal: AbortSignal) => {
  //   const { options, status } = sharedList.current;
  //
  //   if (options.length > 0 && (status === 'loaded' || status === 'failed')) {
  //     const needle = query.trim().toLowerCase();
  //
  //     return needle
  //       ? options.filter(({ title, details }) =>
  //           `${title} ${details ?? ''}`.toLowerCase().includes(needle),
  //         )
  //       : options;
  //   }
  //   ... (backend search as above)
  // }, []);

  // Names known without a request: the portal's saved names plus the 12 opening sites (the
  // default pick is always among them, so it restores with no call).
  const portalStations = useMemo(
    () => mergeStationOptions(portal?.portalFilter.stations ?? [], portal?.openingStations ?? []),
    [portal?.portalFilter.stations, portal?.openingStations],
  );
  const persistPortalStations = portal?.persistPortalStations;

  return useMemo(() => {
    const storageKey = `${collection}.stations`;
    // `page` is `null` when the backend sent no `data` (no sites match): no options.
    const listToOptions = (page: { result: TStationListItem[] } | null) =>
      (page?.result ?? []).map(({ id, generalInfo }) => ({
        id,
        title: generalInfo.name,
        details: formatAddress(generalInfo.location),
      }));

    const knownStations = () =>
      collection === 'portalScope'
        ? portalStations ?? []
        : mergeStationOptions(
            storage.getItem<TEntityOption[]>(storageKey) ?? [],
            portalStations ?? [],
          );

    return {
      lookupStations,

      restoreStations: (ids: string[]): TEntityOption[] | Promise<TEntityOption[]> => {
        const byId = new Map(knownStations().map((station) => [station.id, station]));
        const toLoad = ids.filter((id) => !byId.has(id));

        if (toLoad.length === 0) {
          return ids.map((id) => byId.get(id)).filter(Boolean) as TEntityOption[];
        }

        return (async () => {
          const filter = { station: { stationIds: toLoad } };
          const page = await queryClient.fetchQuery(
            stationListQuery({ page: 0, size: 100, filter }),
          );
          const fetched = listToOptions(page);
          persistPortalStations?.(fetched);
          fetched.forEach((station) => byId.set(station.id, station));
          return ids.map((id) => byId.get(id)).filter(Boolean) as TEntityOption[];
        })();
      },

      persistStations: (stations: TEntityOption[]) => {
        if (collection !== 'portalScope') {
          storage.setItem(storageKey, stations);
        }
        persistPortalStations?.(stations);
      },
    };
  }, [collection, portalStations, persistPortalStations, lookupStations, queryClient]);
};
